import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Sparkles, Headphones, MapPin, Gift, CheckCircle2, Lock, Award, Star, Crown, GraduationCap, Package } from "lucide-react";
import { LevelSeal } from "@/components/LevelSeal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const LEVEL_DETAILS = {
  "Aluno CEAP": {
    icon: GraduationCap,
    seal: null,
    description: "Você faz parte do ecossistema CEAP BRASIL.",
    benefits: ["Carteira digital", "QR Code de presença", "Certificados", "Calendário de cursos"],
  },
  "Aluno Recorrente": {
    icon: Star,
    seal: "bronze",
    description: "Selo Bronze • Você já demonstra recorrência na sua jornada CEAP.",
    benefits: ["Tudo do nível anterior", "Consultoria Online gratuita"],
  },
  "Especialista CEAP": {
    icon: Award,
    seal: "prata",
    description: "Selo Prata • Reconhecimento de profundo conhecimento técnico.",
    benefits: ["Tudo do nível anterior", "Consultoria Presencial em Brasília", "Materiais exclusivos"],
  },
  "Embaixador CEAP": {
    icon: Crown,
    seal: "ouro",
    description: "Selo Ouro • Reconhecimento máximo do CEAP BRASIL.",
    benefits: ["Tudo do nível anterior", "Kit VIP CEAP BRASIL", "Convites exclusivos"],
  },
};

const LEVEL_ORDER = ["Aluno CEAP", "Aluno Recorrente", "Especialista CEAP", "Embaixador CEAP"];

export default function Conquistas() {
  const [data, setData] = useState(null);
  const [kits, setKits] = useState([]);
  const [openKit, setOpenKit] = useState(false);
  const [kitForm, setKitForm] = useState({ address: "", cep: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get("/achievements/me").then(r => setData(r.data)).catch(() => {});
    api.get("/kit-vip/me").then(r => setKits(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return (
    <div className="space-y-3">
      <Skeleton className="h-[180px] w-full rounded-2xl" />
      <Skeleton className="h-[120px] w-full rounded-xl" />
    </div>
  );

  const submitKit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/kit-vip", kitForm);
      toast.success("Solicitação de Kit VIP enviada!");
      setOpenKit(false);
      setKitForm({ address: "", cep: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally { setSubmitting(false); }
  };

  const currentLvl = data.level;
  const currentDetails = LEVEL_DETAILS[currentLvl];
  const activeKit = kits.find(k => ["aguardando_solicitacao", "aprovado", "enviado"].includes(k.status));

  const kitStatusLabel = {
    aguardando_solicitacao: "Aguardando aprovação",
    aprovado: "Aprovado",
    enviado: "Enviado",
    entregue: "Entregue",
    cancelado: "Cancelado",
  };

  return (
    <div className="space-y-5" data-testid="conquistas-page">
      <div className="flex items-center gap-2">
        <Trophy className="w-5 h-5 text-[hsl(var(--gold-foreground))]" />
        <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Minhas Conquistas</h1>
      </div>

      {/* Hero card with current level */}
      <Card className="rounded-2xl overflow-hidden border-[hsl(var(--gold)/0.35)]">
        <div className="bg-wallet-card text-white p-5">
          <div className="flex items-center gap-4">
            {data.seal ? <LevelSeal seal={data.seal} size={64} /> : (
              <div className="w-16 h-16 rounded-full bg-white/10 ring-1 ring-white/15 flex items-center justify-center">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/70">Nível atual</p>
              <h2 className="font-display font-semibold text-2xl" data-testid="current-level">{currentLvl}</h2>
              <p className="text-xs text-white/70 mt-0.5">{currentDetails?.description}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-5">
            <div className="rounded-xl bg-white/8 ring-1 ring-white/15 p-3">
              <p className="text-[10px] uppercase tracking-wider text-white/70">Horas</p>
              <p className="font-display font-semibold text-xl">{data.hours_accumulated}h</p>
            </div>
            <div className="rounded-xl bg-white/8 ring-1 ring-white/15 p-3">
              <p className="text-[10px] uppercase tracking-wider text-white/70">Cursos</p>
              <p className="font-display font-semibold text-xl">{data.courses_completed}</p>
            </div>
            <div className="rounded-xl bg-white/8 ring-1 ring-white/15 p-3">
              <p className="text-[10px] uppercase tracking-wider text-white/70">Frequência</p>
              <p className="font-display font-semibold text-xl">{data.average_frequency}%</p>
            </div>
          </div>
        </div>
        {data.progress?.next_level && (
          <CardContent className="p-5 bg-white">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Próximo nível</p>
              <p className="font-display font-semibold text-sm text-[hsl(var(--primary))]">{data.progress.next_level}</p>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex-1 h-2.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${data.progress.percent}%`, background: "linear-gradient(90deg, #c9a961 0%, #f1d68b 100%)" }}
                />
              </div>
              <span className="font-mono-id text-xs text-[hsl(var(--primary))]">{data.progress.percent}%</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>Faltam <strong className="text-[hsl(var(--primary))]">{data.progress.hours_remaining}h</strong></span>
              <span className="text-[hsl(var(--gold-foreground))]">🎁 {data.progress.reward_label}</span>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Benefits */}
      <Card className="rounded-xl">
        <CardContent className="p-5">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Benefícios desbloqueados
          </h3>
          <ul className="space-y-2">
            <li className="flex items-center gap-3 p-2 rounded-lg border border-[hsl(var(--border))]">
              <div className="w-9 h-9 rounded-lg bg-[hsl(var(--accent)/0.1)] text-[hsl(var(--accent))] flex items-center justify-center"><Headphones className="w-4 h-4" /></div>
              <div className="flex-1">
                <p className="font-medium text-sm">Consultoria Online</p>
                <p className="text-[11px] text-muted-foreground">Aluno Recorrente em diante</p>
              </div>
              {data.benefits_unlocked.consultancy_online ? (
                <Link to="/consultorias"><Button size="sm" variant="outline" data-testid="benefit-consultancy-online"><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Solicitar</Button></Link>
              ) : (
                <Badge variant="outline" className="text-muted-foreground"><Lock className="w-3 h-3 mr-1" /> Bloqueada</Badge>
              )}
            </li>
            <li className="flex items-center gap-3 p-2 rounded-lg border border-[hsl(var(--border))]">
              <div className="w-9 h-9 rounded-lg bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold-foreground))] flex items-center justify-center"><MapPin className="w-4 h-4" /></div>
              <div className="flex-1">
                <p className="font-medium text-sm">Consultoria Presencial</p>
                <p className="text-[11px] text-muted-foreground">Sede CEAP em Brasília • Especialistas+</p>
              </div>
              {data.benefits_unlocked.consultancy_presencial ? (
                <Link to="/consultorias"><Button size="sm" variant="outline" data-testid="benefit-consultancy-presencial"><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Agendar</Button></Link>
              ) : (
                <Badge variant="outline" className="text-muted-foreground"><Lock className="w-3 h-3 mr-1" /> Bloqueada</Badge>
              )}
            </li>
            <li className="flex items-center gap-3 p-2 rounded-lg border border-[hsl(var(--border))]">
              <div className="w-9 h-9 rounded-lg bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold-foreground))] flex items-center justify-center"><Package className="w-4 h-4" /></div>
              <div className="flex-1">
                <p className="font-medium text-sm">Kit VIP CEAP BRASIL</p>
                <p className="text-[11px] text-muted-foreground">Exclusivo Embaixadores</p>
              </div>
              {data.benefits_unlocked.kit_vip ? (
                activeKit ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{kitStatusLabel[activeKit.status]}</Badge>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setOpenKit(true)} data-testid="benefit-kit-vip"><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Solicitar</Button>
                )
              ) : (
                <Badge variant="outline" className="text-muted-foreground"><Lock className="w-3 h-3 mr-1" /> Bloqueado</Badge>
              )}
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Levels jornada */}
      <Card className="rounded-xl">
        <CardContent className="p-5">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] mb-3">Jornada CEAP BRASIL</h3>
          <ul className="space-y-3">
            {LEVEL_ORDER.map(lvl => {
              const det = LEVEL_DETAILS[lvl];
              const Icon = det.icon;
              const idx = LEVEL_ORDER.indexOf(lvl);
              const currentIdx = LEVEL_ORDER.indexOf(currentLvl);
              const reached = idx <= currentIdx;
              return (
                <li key={lvl} className="flex items-center gap-3">
                  {det.seal ? (
                    <LevelSeal seal={det.seal} size={36} className={reached ? "" : "opacity-40 grayscale"} />
                  ) : (
                    <div className={"w-9 h-9 rounded-full flex items-center justify-center " + (reached ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))] text-muted-foreground")}>
                      <Icon className="w-4 h-4" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={"font-display font-medium " + (reached ? "text-[hsl(var(--primary))]" : "text-muted-foreground")}>{lvl}</p>
                    <p className="text-[11px] text-muted-foreground">{det.benefits.join(" • ")}</p>
                  </div>
                  {reached && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {/* Kit VIP history */}
      {kits.length > 0 && (
        <Card className="rounded-xl">
          <CardContent className="p-5">
            <h3 className="font-display font-semibold text-[hsl(var(--primary))] mb-3 flex items-center gap-2"><Package className="w-4 h-4" /> Kits VIP</h3>
            <ul className="space-y-2">
              {kits.map(k => (
                <li key={k.id} className="text-sm border border-[hsl(var(--border))] rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="capitalize">{kitStatusLabel[k.status]}</Badge>
                    {k.tracking_code && <span className="font-mono-id text-xs text-muted-foreground">{k.tracking_code}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{k.address}</p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Dialog open={openKit} onOpenChange={setOpenKit}>
        <DialogContent className="max-w-[460px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>Solicitar Kit VIP CEAP BRASIL</DialogTitle>
            <DialogDescription>Confirme o endereço de entrega. Após aprovação, você acompanhará o status.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitKit} className="space-y-3">
            <div><Label>Endereço completo</Label><Textarea required rows={3} value={kitForm.address} onChange={(e) => setKitForm(f => ({ ...f, address: e.target.value }))} placeholder="Rua, número, bairro, cidade/UF" data-testid="kit-address" /></div>
            <div><Label>CEP</Label><Input value={kitForm.cep} onChange={(e) => setKitForm(f => ({ ...f, cep: e.target.value }))} placeholder="00000-000" data-testid="kit-cep" /></div>
            <div><Label>Observações</Label><Textarea rows={2} value={kitForm.notes} onChange={(e) => setKitForm(f => ({ ...f, notes: e.target.value }))} placeholder="Tamanho de camiseta, referências, etc." /></div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="kit-submit">{submitting ? "Enviando…" : "Confirmar solicitação"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
